<template>
    <div id="review-page">
        <loading-overlay :active.sync="displayLoader" loader="dots" color="#1A74DB" height="30" width="100%" opacity="0.85">
            <template v-if="displayContractMessage" v-slot:after>
                <h3 id="loading-notice">{{ checkoutContent.pageLoadingText.generating_contract}}</h3>
            </template>
        </loading-overlay>
        <div v-if="pageDataFetched">
            <checkout-header
                :categoryFilter="{display: false}"
                :shoppingBag="{display: false}"
                @contact-us="showContactUsModal = true"
                :activePage="'review'">
            </checkout-header>
            <EditBilling v-if="baselinePhoenixChanges && showEditBillingModal" @hide-modal="showEditBillingModal = false" :title="renewalsPersistedVariables.jsonContent.text.editYourBillingInformation" :button-text="renewalsPersistedVariables.jsonContent.buttons.submit" />
            <ContactUs v-if="baselinePhoenixChanges && showContactUsModal"  @hide-modal="showContactUsModal = false" :button-text="renewalsPersistedVariables.jsonContent.buttons.submit" :locale="locale"></ContactUs>
            <AdjustQuantities v-if="baselinePhoenixChanges && showAdjustQuantitiesModal" @hide-modal="showAdjustQuantitiesModal = false" :productPrices="productPrices" :locale="locale"></AdjustQuantities>
            <div id="review-body">
                <div id="order-shipping" class="row">
                    <address-confirmation
                        v-if="!baselinePhoenixChanges"
                        class="col xs-12 md-6"
                        :address="shipping"
                        :title="checkoutSharedContent.opportunityInfoHeaders.ship_to"
                        :editInfoText="renewalsPersistedVariables.jsonContent.buttons.editInfo">
                    </address-confirmation>
                    <address-confirmation
                        v-if="(!isExistingCustomer || baselinePhoenixChanges)"
                        class="col xs-12 md-6"
                        @editBilling="showEditBillingModal = true"
                        :address="billing"
                        :title="checkoutSharedContent.opportunityInfoHeaders.bill_to"
                        :editInfoText="renewalsPersistedVariables.jsonContent.buttons.editInfo">
                    </address-confirmation>
                </div>
                <payment-plan-container @scrollToPricingBreakdown=scrollToPricingBreakdown @toggleTerm="toggleTerm"></payment-plan-container>
                <div class="scroll-pricing-breakdown" ref="pricingBreakdown">
                    <pricing-breakdown :pageLoaded="pageDataFetched" @adjustQuantities="showAdjustQuantitiesModal = true" @productPrices="setProductPrices"></pricing-breakdown>
                </div>
                <template v-if="selectedPaymentType && !hasExceededFinanceLimit">
                    <keep-alive>
                     <payment-method
                        class="margin-top-20"
                        :ownerEmail="ownerEmail"
                        :availablePaymentMethods="availablePaymentMethods"
                        @update-send-pdf="handlePDFChange"
                        ></payment-method>
                    </keep-alive>
                    <div v-if="checkoutType === 'ExpressCheckout' && !isRenewal">
                        <div class="row">
                            <div class="col xs-12" v-html="checkoutContent.dayRiskFree"></div>
                        </div>
                    </div>
                    <agreement
                        ref="termsOfAgreement"
                        :agreementText="agreementText"
                        :confirmationText="checkoutSharedContent.agreeToTerms">
                    </agreement>
                </template>
                <div class="row" id="checkout-button-container">
                    <div class="col xs-12 flex flex-around">
                        <button id="checkout-button" @click="submitReviewPageHandler" :disabled="submissionInProgress">{{ submitButtonText }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters, mapMutations, mapActions } from 'vuex';
    import axios from 'axios';
    import CheckoutHeader from '../../layouts/header';
    import AddressConfirmation from '../address-confirmation';
    import PaymentPlanContainer from '../payment-plan-container';
    import PricingBreakdown from '../pricing-breakdown';
    import PaymentMethod from '../payment-method';
    import Agreement from '../agreement';
    import EditBilling from '../edit-billing-form.vue'
    import ScrollToTop from '../../../../mixins/scrollToTop';
    import SalesforceResources from "../../../../mixins/salesforce-resources";
    import SegmentResources from "../../../../mixins/segment-resources";
    import ContactUs from '../contact-us'
    import AdjustQuantities from '../adjust-cart-quantities-form'
    import { analyticsTrack } from '../../../../plugins/segment';
    import { getIPForVisitor } from '../../../../../bundled_js/get-ip-address';
    import RenewalsResources from '../../../../mixins/renewals-resources';
    import { determine_locale } from '../../../../plugins/contentful';

    export default {

        mixins: [
            ScrollToTop,
            SalesforceResources,
            SegmentResources,
            RenewalsResources
        ],

        components: {
            CheckoutHeader,
            AddressConfirmation,
            PaymentPlanContainer,
            PricingBreakdown,
            PaymentMethod,
            Agreement,
            EditBilling,
            ContactUs,
            AdjustQuantities
        },

        data() {
            return {
                pageDataFetched: false,
                submissionInProgress: false,
                hideContractMessage: false,
                ownerEmail: '',
                availablePaymentMethods: [],
                sendPDF: false,
                showEditBillingModal: false,
                showContactUsModal: false,
                showAdjustQuantitiesModal: false,
                productPrices: {},
                locale: "en-US"
            }
        },

        computed: {
            ...mapGetters([
                'shouldShowSinglePlan',
                'isPhoenixCustomer',
                'isPhoenixPhaseTwoCustomer',
                'isPhoenixPhaseThreeCustomer',
                'applicationVersion',
                'typeOfRenewal',
                'productSource'
            ]),

            ...mapGetters('checkout', [
                'accountId',
                'opptyId',
                'isSelfServiced',
                'isExistingCustomer',
                'paymentMethod',
                'selectedPaymentType',
                'checkoutType',
                'isRenewal',
                'smallFleetOpportunity',
                'initialPaymentTerm',
                'currencyIsoCode',
                'licenseTerm',
                'renewalTerm',
                'creditLimit',
                'creditLimitExceeded',
                'isENT',
                'checkoutCompleted',
                'writtenLanguage',
            ]),

            ...mapGetters('shoppingCart', [
                'isSoftwareCartEmpty',
                'productCostsInfo',
                'transactionLimitExceeded',
                'transactionTotal',
                'consolidatedMmyData',
                'consolidatedVinData',
                'consolidatedManualSelectionData'
            ]),

            ...mapGetters('content', [
                'checkoutSharedContent',
                'checkoutContent',
                'webstoreContent',
                'renewalsPersistedVariables'
            ]),

            ...mapGetters('shipping', [
                'shipping'
            ]),

            ...mapGetters('billing', [
                'billing'
            ]),

            displayLoader() {
                return !this.pageDataFetched || this.submissionInProgress;
            },

            displayContractMessage() {
                return this.shouldGenerateContract && !this.isSoftwareCartEmpty && this.submissionInProgress && !this.hideContractMessage
            },

            agreementText() {
                if (this.isRenewal && this.checkoutType.toLowerCase() === 'midmarketcheckout'){
                    return this.checkoutSharedContent.renewalMidmarketText;
                } else if (this.isRenewal && this.checkoutType.toLowerCase() === 'expresscheckout'){
                    return this.checkoutSharedContent.renewalExpressText.replace("license_term", this.licenseTerm / 12);
                } else if (this.checkoutType.toLowerCase() === 'midmarketcheckout'){
                    return this.checkoutSharedContent.sharedAcceptanceOfAgreement;
                } else {
                    return this.checkoutContent.acceptanceOfAgreement.replace("license_term", this.licenseTerm / 12);
                }
            },

            shouldGenerateContract() {
                return this.checkoutContent.docusignFeatureFlag && ["USD", "CAD", "MXN", "GBP"].includes(this.currencyIsoCode) && (this.checkoutType === "ExpressCheckout" || this.isSelfServiced);
            },

            hasExceededFinanceLimit() {
                return this.creditLimitExceeded || this.transactionLimitExceeded;
            },

            submitButtonText() {
                if (this.hasExceededFinanceLimit) {
                    return this.checkoutContent.selectPaymentInfo.confirm;
                } else if (this.shouldGenerateContract && !this.isSoftwareCartEmpty && !this.sendPDF) {
                    return this.checkoutSharedContent.continueToSignature;
                } else {
                    return this.checkoutSharedContent.continueToPayment;
                }
            },
        },

        methods: {
            ...mapMutations([
                'setCurrencySymbol',
                'setPhoenixPhaseTwoCustomer',
                'setPhoenixCustomer',
                'setTypeOfRenewal',
                'setApplicationVersion',
                'setProductSource',
                'setPhoenixPhaseThreeCustomer',
                'setClickpathCotermingEnabled'
            ]),

            ...mapMutations('checkout', [
                'setAccountId',
                'setOpptyId',
                'setIsSelfServiced',
                'setIsENT',
                'setCheckoutType',
                'setContractEndDate',
                'setCurrencyIsoCode',
                'setIsExistingCustomer',
                'setLicenseTerm',
                'setRenewalTerm',
                'setSmallFleetOpportunity',
                'setInitialPaymentTerm',
                'setSelectedPaymentType',
                'setIsPmUpdate',
                'setApprovedPaymentType',
                'setCreditLimit',
                'setIsRenewal',
                'setPaymentMethod',
                'setCheckoutCompleted',
                'setWrittenLanguage'
            ]),

            ...mapMutations('shoppingCart', [
                'setIsSoftwareCartEmpty',
                'setCablesInCartCount',
                'setProductCostsInfo',
                'setHardwareCart',
                'setSoftwareCart',
                'setRenewalDiscountMatrix'
            ]),

            ...mapActions('content',[
                'getCheckoutContent',
                'getRenewalsPersistedVariables'
            ]),

            ...mapMutations('shipping', [
                'setShipping'
            ]),

            ...mapMutations('billing', [
                'setBilling'
            ]),

            initializeReviewStoreData: async function(reviewPageData) {
                this.setIsSelfServiced(reviewPageData.data.is_self_serviced);
                this.setIsENT(reviewPageData.data.is_ENT);
                this.setIsExistingCustomer(reviewPageData.data.is_existing_customer);
                this.setShipping(reviewPageData.data.shipping);
                this.setBilling(reviewPageData.data.billing);
                this.setCheckoutType(reviewPageData.data.checkout_type);
                this.setCurrencyIsoCode(reviewPageData.data.currency_code);
                this.setIsSoftwareCartEmpty(reviewPageData.data.is_software_cart_empty);
                this.setProductCostsInfo(reviewPageData.data.product_costs_info);
                this.setCurrencySymbol(reviewPageData.data.currency_symbol);
                this.setSmallFleetOpportunity(reviewPageData.data.small_fleet_opportunity);
                this.setInitialPaymentTerm(reviewPageData.data.initial_payment_term);
                this.setSelectedPaymentType(reviewPageData.data.selected_payment_type);
                this.setHardwareCart(reviewPageData.data.hardware_cart);
                this.setSoftwareCart(reviewPageData.data.software_cart);
                this.setIsPmUpdate(reviewPageData.data.is_pm_update);
                this.setCablesInCartCount(reviewPageData.data.cables_in_cart_count);
                this.setApprovedPaymentType(reviewPageData.data.approved_payment_type);
                this.setCreditLimit(reviewPageData.data.credit_limit);
                this.setIsRenewal(reviewPageData.data.is_renewal);
                this.setPhoenixPhaseTwoCustomer(reviewPageData.data.phoenix_phase_two_customer);
                this.setPhoenixPhaseThreeCustomer(reviewPageData.data.phoenix_phase_three_customer);
                this.setCheckoutCompleted(reviewPageData.data.checkout_completed);
                if(this.isPhoenixPhaseTwoCustomer) {
                    this.setLicenseTerm(this.renewalTerm);
                } else {
                    this.setLicenseTerm(reviewPageData.data.license_term);
                }
                this.setClickpathCotermingEnabled(reviewPageData.data.clickpath_coterming_enabled);
                this.setRenewalDiscountMatrix(reviewPageData.data.renewal_discount_matrix);
                this.setContractEndDate(reviewPageData.data.contract_end_date);
                this.setPhoenixCustomer(reviewPageData.data.phoenix_customer);
                this.setTypeOfRenewal(reviewPageData.data.type_of_renewal_customer);
                this.setApplicationVersion(reviewPageData.data.version);
                this.setOpptyId(reviewPageData.data.oppty_id);
                this.setAccountId(reviewPageData.data.account_id);
                this.setProductSource(reviewPageData.data.product_source);
                this.setWrittenLanguage(reviewPageData.data.written_language);
                this.locale = determine_locale(this.writtenLanguage, this.billing.country);
                this.ownerEmail = reviewPageData.data.owner_email
                this.availablePaymentMethods = reviewPageData.data.available_payment_methods
                
                await this.getCheckoutContent();
                await this.getRenewalsPersistedVariables();
            },

            routeToErrorComponent(errorType) {
                this.$router.push({path: '/message', query: {type: errorType}});
            },

            handlePDFChange(value) {
                this.sendPDF = value;
            },
            setProductPrices(value){
                this.productPrices = value;
            },
            validateSubmission() {
                if (!this.selectedPaymentType) {
                    this.$fire({
                        title: this.checkoutContent.validationErrorMessages.incompletePaymentType.title,
                        text: this.checkoutContent.validationErrorMessages.incompletePaymentType.text,
                        type: "error"
                    });
                    return false;
                } else if (!this.paymentMethod) {
                    this.$fire({
                        title: this.checkoutContent.validationErrorMessages.incompletePaymentMethod.title,
                        text: this.checkoutContent.validationErrorMessages.incompletePaymentMethod.text,
                        type: "error"
                    });
                    return false;
                } else if (!this.$refs.termsOfAgreement.isChecked) {
                    this.$fire({
                        title: this.checkoutContent.validationErrorMessages.incompleteTermsOfAgreement.title,
                        text: this.checkoutContent.validationErrorMessages.incompleteTermsOfAgreement.text,
                        type: "error"
                    });
                    return false;
                } else if(this.productCostsInfo.order_totals.upfront === 0){
                    this.$fire({
                        title: "No Products in Renewal",
                        text: "It looks like you don't have any products in your order. Please increase the quantity by clicking the \"Adjust Quantities\" button before checking out. ",
                        type: "error"
                    });
                    return false;
                }
                else {
                    return true;
                }

            },

            submitReviewPageHandler: async function () {
                // if you don't have a selected payment type show error notice
                if ((this.transactionLimitExceeded || this.creditLimitExceeded) && !this.selectedPaymentType) {
                    this.$fire({
                        title: this.checkoutContent.validationErrorMessages.incompletePaymentType.title,
                        text: this.checkoutContent.validationErrorMessages.incompletePaymentType.text,
                        type: "error"
                    });
                    return false;
                }
                if (this.transactionLimitExceeded) {
                    await this.submitOrderToAE('transaction');
                } else if (this.creditLimitExceeded) {
                    await this.submitOrderToAE('credit');
                } else {
                    await this.submitReviewData();
                }
            },

            setPageDataFetched(data){
                this.pageDataFetched = data;
            },
            
            async toggleButton(selectedRenewalTerm){
                axios({
                    method: 'post',
                    url: '/checkout/update-license-term',
                    headers: {
                        'Content-type': 'application/json'
                    },
                    data: {
                        renewalTerm: selectedRenewalTerm
                    },
                })
                .then((response) => {
                    this.setProductCostsInfo(response.data.product_costs_info);
                    this.setSoftwareCart(response.data.software_cart);
                    this.pageDataFetched = true;
                })
                .catch(function(error){
                    console.error(error);
                })
            },

            toggleButtonSetters(paymentType, selectedRenewalTerm){
                this.setRenewalTerm(selectedRenewalTerm);
                this.setLicenseTerm(selectedRenewalTerm);
                this.setSelectedPaymentType(paymentType)
            },

            async toggleTerm(selectedRenewalTerm){
                this.pageDataFetched = false;
                if(selectedRenewalTerm === 36){
                    this.toggleButtonSetters('Direct Annual', 36);
                }
                else{
                    this.toggleButtonSetters('Upfront Payments', 12);
                }
                await this.toggleButton(selectedRenewalTerm);
            },

            scrollToPricingBreakdown() {
                this.$refs["pricingBreakdown"].scrollIntoView({ behavior: "smooth" });
            },

            submitOrderToAE: async function(limitExceeded) {
                try {
                    let response = await axios({
                        method: 'POST',
                        url: '/webstore/manual-order-process-request',
                        headers: {'Content-type': 'application/json'},
                        data: { 
                            limitExceeded,
                            vinData: this.consolidatedVinData,
                            mmyData: this.consolidatedMmyData,
                            manualSelectionData: this.consolidatedManualSelectionData,
                        },
                        timeout: 10000
                    })
                    if (response.status !== 200) {
                        this.routeToErrorComponent('submitOrderToAeError');
                        return;
                    }
                } catch (e) {
                    this.routeToErrorComponent('submitOrderToAeError')
                    return;
                }

                if (limitExceeded === 'credit') {
                    analyticsTrack('Credit Limit Exceeded', 
                    {
                        "selectedPaymentType": this.selectedPaymentType,
                        "creditLimit": this.creditLimit,
                        "monthlyCost": this.productCostsInfo.subtotals.software.monthly,
                    })
                } else if (limitExceeded === 'transaction') {
                    analyticsTrack('Transaction Limit Exceeded', 
                    {
                        "transactionTotal": this.transactionTotal,
                    })
                }
                
                this.$router.push({path: "/checkout/confirm"});
                
            },

            submitReviewData: async function() {

                if (!this.validateSubmission()) {
                    return false;
                }
                
                //validate that order total isn't 0. Use case is for renewals if customer adjusts quantity to 0 for some reason.
                
                
                this.submissionInProgress = true;

                let payload = { paymentMethod: this.paymentMethod, "PONumber": this.billing.PONumber, "sendPDF": this.sendPDF, billing: this.billing };
                
                if (!this.shouldShowSinglePlan) {
                    payload.selectedPaymentType = this.selectedPaymentType;
                }

                // is session active ping
                let sessionStatus = null;
                try {
                    sessionStatus = await axios({
                                                    method: 'POST',
                                                    url: '/checkout/session-check',
                                                    headers: {'Content-type': 'application/json'},
                                                    data: {
                                                        page: 'review',
                                                    },
                                                    timeout: 20000
                    });
                } catch(e) {
                    this.$router.push({path: '/message', query: {type: 'errorCheckingOut'}});
                    return;
                }

                // validate session status
                if (!sessionStatus.data.is_active) {
                    this.$router.push({path: '/message', query: {type: 'sessionExpired'}});
                    return;
                }

                try {
                    let response = await axios({
                        method: 'POST',
                        url: '/checkout/submit-review-data',
                        headers: {'Content-type': 'application/json'},
                        data: payload,
                        timeout: 20000
                    });
                    if (response.status !== 200) {
                        this.routeToErrorComponent('errorCheckingOut');
                        return;
                    } else {
                        analyticsTrack('Review Confirmed', 
                        {
                            "payment_method": this.paymentMethod,
                        });
                    }
                } catch(e) {
                    this.routeToErrorComponent('errorCheckingOut');
                    return;
                }
 
                analyticsTrack("Continue to Signature Button Clicked", {"accountId": this.accountId, "opptyId": this.opptyId, "version": this.applicationVersion, "phase": this.typeOfRenewal, "source": this.productSource});

                // Short-circuit, skip docusign api call if non-express checkout or currency not supported for gen and send
                // Also skip for customers selecting send in PDF of Product Order instead of Order Form

                if (!this.shouldGenerateContract || this.sendPDF) {
                    this.$router.push('/checkout/payment');
                    return;
                }

                // docusign
                try {
                    let response = await axios({url: '/docusign/generate-contract', timeout: 30000})
                    if (response.status === 200) {
                        if (response.data.api_status && response.data.contract_url) {
                            window.location.href = response.data.contract_url;
                            return;
                        } else {
                            this.$router.push('/checkout/payment');
                            return;
                        }
                    } else {
                        throw "unhandledStatusCode";
                    }
                } catch(e) {
                    this.routeToErrorComponent('errorCheckingOut');
                    return;
                }
            }, 

        },
        async created() {
            try {
                let reviewPageData = await axios({
                    method: 'POST',
                    url: '/checkout/review-page-data',
                    headers: {'Content-type': 'application/json'},
                    data: {
                        storeShipping: this.shipping,
                        renewalTerm: this.renewalTerm,
                        storeBilling: this.billing,
                    },
                    timeout: 20000
                });
                await this.initializeReviewStoreData(reviewPageData);
                await this.$nextTick();

                axios({
                    method: 'POST',
                    url: '/checkout/page-viewed-tracking',
                    headers: {'Content-type': 'application/json'},
                    data: {
                        analyticsId: this.getAnalyticsCookie(),
                        pageName: "Review"
                    }
                });
                analyticsTrack("Page Viewed", {"accountId": this.accountId, "opptyId": this.opptyId, "version": this.applicationVersion, "phase": this.typeOfRenewal, "source": this.productSource, "page_name": "Review", "ip": await getIPForVisitor()});
                this.pageDataFetched = true;
            } catch(error) {
                if (error.message.includes('timeout')) {
                    this.routeToErrorComponent('errorCheckingOut');
                    return;
                }
                let errorType = error.response.data;

                if (errorType === 'sessionExpired') {
                    this.$router.push({path: '/message', query: {type: 'sessionExpired'}});
                    return
                } else if (errorType === 'shipping_and_handling_error') {
                    this.routeToErrorComponent('shippingAndHandlingError');
                    return;
                } else if (errorType === 'tax_error') {
                    this.routeToErrorComponent('taxError');
                    return;
                } 
                else if(errorType === 'checkout_completed'){
                    this.$router.push({path: '/message', query: {type: 'orderConfirmed'}});
                }
                else {
                    this.routeToErrorComponent('errorCheckingOut');
                    return;
                }
            }
        }
    }
</script>

<style scoped>

    .flex {
        display: flex;
    }

    .flex-around {
        justify-content: space-around;
    }

    #review-page {
        width: 100%;
        height: 100%;
        font-family: "Nitti Grotesk", sans-serif;
        font-style: normal;
        font-weight: normal;
        margin: 0 auto;
    }

    #loading-notice {
        text-align: center;
    }

    #review-body {
        max-width: 1080px;
        min-width: 320px;
        margin: 0 auto;
        margin-top: 24px;
    }

    #order-shipping {
        margin-top: 60px;
    }

    .margin-top-20 {
        margin-top: 20px;
    }


    #shipping-billing-equal {
        margin-right: 2px;
        margin-left: 3px;
    }

    #billing-info {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
    }

    #checkout-button-container {
        margin: 50px 0px;
    }

    #checkout-button {
        height: 50px;
        color: white;
        background: #1A74DB;
        border-radius: 4px;
        border: none;
        font-size: 20px;
        line-height: 20px;
        padding: 0px 40px;
    }

    #checkout-button:hover {
        cursor: pointer;
        background-color: #0d6ede;
        border: #0d6ede;
    }

    #checkout-button:disabled {
        background-color: #657281;
    }
    .scroll-pricing-breakdown {
        scroll-margin-top: 60px;
    }
    

</style>
